import { Package, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api.ts';
import type { ClubSeries } from '../../lib/equipment.ts';
import { addSeries, listSeries } from '../../lib/equipment.ts';

interface Props {
    onClose: () => void;
    onAdded: () => void;
}

/** Rigidités telles que Decathlon les nomme, avec leur code de shaft.
 *
 *  Le vocabulaire du magasin d'abord — « vitesse lente » est ce qui est écrit
 *  sur la fiche produit — et la lettre normalisée à côté, parce que c'est elle
 *  qui figure sur le shaft. Chaque série n'en propose qu'une partie : Inesis
 *  confond Lady et Senior sous la lettre A, Titleist les sépare. */
const FLEXES = [
    { value: 'L', label: 'Vitesse très lente', hint: 'Ladies' },
    { value: 'A', label: 'Vitesse lente', hint: 'Lady / Senior' },
    { value: 'R', label: 'Vitesse moyenne', hint: 'Regular' },
    { value: 'S', label: 'Vitesse rapide', hint: 'Stiff' },
    { value: 'X', label: 'Vitesse très rapide', hint: 'X-Stiff' },
] as const;

function flexesOf(s: ClubSeries) {
    return FLEXES.filter((f) => s.flexes.includes(f.value)).map((f) =>
        f.value === 'A' && s.flexes.includes('L') ? { ...f, hint: 'Senior' } : f);
}

const clubs = (n: number): string => `${n} club${n > 1 ? 's' : ''}`;

const SIZES = [
    { value: '1', label: 'Taille 1', hint: 'la plus courte' },
    { value: '2', label: 'Taille 2', hint: 'la plus longue' },
] as const;

export function SeriesPicker({ onClose, onAdded }: Props) {
    const [series, setSeries] = useState<ClubSeries[] | null>(null);
    const [selected, setSelected] = useState<ClubSeries | null>(null);
    const [flex, setFlex] = useState<string>('R');
    const [size, setSize] = useState<'1' | '2'>('2');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState<{ added: number; skipped: number } | null>(null);

    useEffect(() => {
        void listSeries().then(setSeries).catch((e: Error) => setError(e.message));
    }, []);

    const confirm = async (): Promise<void> => {
        if (!selected) return;
        setBusy(true);
        setError(null);
        try {
            const r = await addSeries(selected.id, {
                shaftFlex: flex, size: selected.sizesOffered ? size : null,
            });
            setDone(r);
            onAdded();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Ajout impossible');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center">
            <div className="max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-t-box bg-base-200 sm:rounded-box">
                <div className="sticky top-0 flex items-center justify-between bg-base-200 px-4 py-3">
                    <h2 className="text-lg font-bold">
                        {selected ? selected.brand + ' ' + selected.name : 'Ajouter une série'}
                    </h2>
                    <button type="button" className="btn btn-ghost btn-square" onClick={onClose}
                        aria-label="Fermer">
                        <X size={20} />
                    </button>
                </div>

                <div className="space-y-3 px-4 pb-4">
                    {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

                    {/* ── Confirmation ─────────────────────────────────────── */}
                    {done ? (
                        <>
                            <div role="status" className="alert alert-success text-sm">
                                <span>
                                    {done.added} club{done.added > 1 ? 's' : ''} ajouté{done.added > 1 ? 's' : ''}
                                    {done.skipped > 0 && ` · ${done.skipped} déjà présent${done.skipped > 1 ? 's' : ''}, laissé${done.skipped > 1 ? 's' : ''} tel${done.skipped > 1 ? 's' : ''} quel${done.skipped > 1 ? 's' : ''}`}
                                </span>
                            </div>
                            <button type="button" className="btn btn-primary btn-block tap-target"
                                onClick={onClose}>Terminé</button>
                        </>
                    ) : selected ? (
                        /* ── Options de l'exemplaire ──────────────────────── */
                        <>
                            <p className="text-sm opacity-70">{selected.description}</p>

                            <div className="rounded-box bg-base-100 p-3">
                                <div className="mb-2 text-xs font-semibold uppercase tracking-wider opacity-50">
                                    {clubs(selected.items.length)}
                                </div>
                                <ul className="space-y-1 text-sm">
                                    {selected.items.map((i) => (
                                        <li key={i.position} className="flex justify-between gap-3">
                                            <span>{i.label}</span>
                                            <span className="tabular-nums opacity-60">
                                                {i.loftDeg != null ? `${i.loftDeg}°` : '—'}
                                                {i.lieAngleDeg != null && ` · lie ${i.lieAngleDeg}°`}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* La rigidité et la taille ne changent pas les
                                lofts chez Inesis : ce sont des propriétés de
                                votre exemplaire, demandées ici plutôt que
                                portées par la série. */}
                            <fieldset>
                                <legend className="mb-1 text-sm font-medium">Rigidité du shaft</legend>
                                <div className="grid grid-cols-3 gap-2">
                                    {flexesOf(selected).map((f) => (
                                        <button key={f.value} type="button"
                                            onClick={() => setFlex(f.value)}
                                            aria-pressed={flex === f.value}
                                            className={`rounded-field px-2 py-2 text-center text-xs transition-colors ${
                                                flex === f.value ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                                            }`}>
                                            <span className="block font-semibold">{f.label}</span>
                                            <span className="block opacity-70">{f.hint} · {f.value}</span>
                                        </button>
                                    ))}
                                </div>
                            </fieldset>

                            {selected.sizesOffered && <fieldset>
                                <legend className="mb-1 text-sm font-medium">Taille</legend>
                                <div className="grid grid-cols-2 gap-2">
                                    {SIZES.map((s) => (
                                        <button key={s.value} type="button"
                                            onClick={() => setSize(s.value)}
                                            aria-pressed={size === s.value}
                                            className={`rounded-field px-2 py-2 text-center text-xs transition-colors ${
                                                size === s.value ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                                            }`}>
                                            <span className="block font-semibold">{s.label}</span>
                                            <span className="block opacity-70">{s.hint}</span>
                                        </button>
                                    ))}
                                </div>
                            </fieldset>}

                            <p className="text-xs opacity-60">
                                Les lofts viennent des fiches du fabricant. Vérifiez celui gravé sur vos
                                clubs et corrigez si besoin — c'est l'angle réel qui sert aux
                                statistiques.
                            </p>

                            <div className="flex gap-2 pt-1">
                                <button type="button" className="btn btn-ghost flex-1"
                                    onClick={() => setSelected(null)}>Retour</button>
                                <button type="button" className="btn btn-primary flex-1 tap-target"
                                    disabled={busy} onClick={() => void confirm()}>
                                    {busy ? <span className="loading loading-spinner" />
                                        : selected.items.length === 1 ? 'Ajouter ce club'
                                        : `Ajouter ${clubs(selected.items.length)}`}
                                </button>
                            </div>
                        </>
                    ) : (
                        /* ── Choix de la série ────────────────────────────── */
                        <>
                            {!series && (
                                <div className="flex justify-center py-8">
                                    <span className="loading loading-ring loading-lg text-primary" />
                                </div>
                            )}
                            {series?.map((s) => (
                                <button key={s.id} type="button" onClick={() => {
                                    setSelected(s);
                                    setFlex(s.flexes.includes('R') ? 'R' : (s.flexes[0] ?? 'R'));
                                }}
                                    className="flex w-full items-start gap-3 rounded-box bg-base-100 p-4 text-left transition-colors hover:bg-base-300">
                                    <Package size={22} className="mt-0.5 shrink-0 text-primary" />
                                    <span className="min-w-0">
                                        <span className="block font-semibold">
                                            {s.brand} {s.name}
                                            {s.variantLabel && (
                                                <span className="ml-2 badge badge-ghost badge-sm">{s.variantLabel}</span>
                                            )}
                                        </span>
                                        <span className="mt-0.5 block text-xs opacity-60">
                                            {clubs(s.items.length)} ·{' '}
                                            {s.items.map((i) => i.label.replace('Pitching Wedge', 'PW')).join(', ')}
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
